<template>
  <div class="bdp-wrap" ref="root">
    <div
      class="bdp-trigger"
      :class="{ 'is-open': showCalendar, 'is-empty': !value }"
      @click="toggleCalendar"
    >
      <fa-icon :icon="['fas', 'calendar-alt']" class="bdp-icon" />
      <span class="bdp-val">{{ value || 'YYYY-MM-DD' }}</span>
      <fa-icon
        v-if="value"
        :icon="['fas', 'times']"
        class="bdp-clear-icon"
        @click.stop="clearDate"
      />
    </div>

    <div v-if="showCalendar" class="bdp-calendar" :style="calStyle" @click.stop>

      <div class="bdp-nav">
        <button type="button" class="bdp-nav-btn" @click="prevMonth">
          <fa-icon :icon="['fas', 'chevron-left']" />
        </button>
        <div class="bdp-nav-selects">
          <select v-model.number="calMonth" class="bdp-sel">
            <option v-for="(m, i) in MONTHS" :key="i" :value="i">{{ m }}</option>
          </select>
          <select v-model.number="calYear" class="bdp-sel">
            <option v-for="y in yearRange" :key="y" :value="y">{{ y }}</option>
          </select>
        </div>
        <button type="button" class="bdp-nav-btn" @click="nextMonth">
          <fa-icon :icon="['fas', 'chevron-right']" />
        </button>
      </div>

      <div class="bdp-grid">
        <div v-for="d in DAYS" :key="d" class="bdp-weekday">{{ d }}</div>
        <div v-for="n in leadingBlanks" :key="'b' + n" class="bdp-cell"></div>
        <button
          v-for="day in daysInMonth"
          :key="day"
          type="button"
          class="bdp-cell bdp-day"
          :class="{
            'is-selected': isSelected(day),
            'is-today': isToday(day)
          }"
          @click="selectDay(day)"
        >{{ day }}</button>
      </div>

      <div class="bdp-footer">
        <button type="button" class="bdp-foot-btn" @click="selectToday">Today</button>
        <button type="button" class="bdp-foot-btn bdp-foot-btn--clear" @click="clearDate">Clear</button>
      </div>

    </div>
  </div>
</template>

<script>
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December']
const DAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']

export default {
  name: 'BillingDatePicker',

  props: {
    value: { type: String, default: '' } // YYYY-MM-DD
  },

  data () {
    const now = new Date()
    return {
      MONTHS,
      DAYS,
      showCalendar: false,
      calMonth: now.getMonth(),
      calYear: now.getFullYear(),
      calStyle: {}
    }
  },

  computed: {
    daysInMonth () {
      return new Date(this.calYear, this.calMonth + 1, 0).getDate()
    },
    leadingBlanks () {
      return new Date(this.calYear, this.calMonth, 1).getDay()
    },
    yearRange () {
      const base = new Date().getFullYear()
      const years = []
      for (let y = base - 100; y <= base + 20; y++) years.push(y)
      return years
    }
  },

  watch: {
    value: {
      immediate: true,
      handler (val) {
        if (!val) return
        const parts = val.split('-')
        if (parts.length === 3 && !isNaN(+parts[0])) {
          this.calYear = parseInt(parts[0])
          this.calMonth = parseInt(parts[1]) - 1
        }
      }
    }
  },

  mounted () {
    document.addEventListener('click', this.onOutsideClick)
    document.addEventListener('scroll', this.onScroll, true)
    window.addEventListener('resize', this.onResize)
  },

  beforeDestroy () {
    document.removeEventListener('click', this.onOutsideClick)
    document.removeEventListener('scroll', this.onScroll, true)
    window.removeEventListener('resize', this.onResize)
  },

  methods: {
    toggleCalendar () {
      if (this.showCalendar) {
        this.showCalendar = false
      } else {
        this.updatePosition()
        this.showCalendar = true
      }
    },

    updatePosition () {
      const rect = this.$refs.root.getBoundingClientRect()
      const calH = 290
      const spaceBelow = window.innerHeight - rect.bottom
      const top = spaceBelow >= calH ? rect.bottom + 4 : rect.top - calH - 4
      this.calStyle = {
        position: 'fixed',
        top: Math.max(8, top) + 'px',
        left: rect.left + 'px',
        zIndex: '9999'
      }
    },

    onOutsideClick (e) {
      if (this.$refs.root && !this.$refs.root.contains(e.target)) {
        this.showCalendar = false
      }
    },

    onScroll () {
      if (this.showCalendar) this.updatePosition()
    },

    onResize () {
      if (this.showCalendar) this.updatePosition()
    },

    prevMonth () {
      if (this.calMonth === 0) { this.calMonth = 11; this.calYear-- } else this.calMonth--
    },

    nextMonth () {
      if (this.calMonth === 11) { this.calMonth = 0; this.calYear++ } else this.calMonth++
    },

    selectDay (day) {
      const yyyy = this.calYear
      const mm = String(this.calMonth + 1).padStart(2, '0')
      const dd = String(day).padStart(2, '0')
      this.$emit('input', `${yyyy}-${mm}-${dd}`)
      this.showCalendar = false
    },

    selectToday () {
      const now = new Date()
      this.calMonth = now.getMonth()
      this.calYear = now.getFullYear()
      this.selectDay(now.getDate())
    },

    clearDate () {
      this.$emit('input', '')
      this.showCalendar = false
    },

    isSelected (day) {
      if (!this.value) return false
      const parts = this.value.split('-')
      return parseInt(parts[2]) === day &&
        parseInt(parts[1]) - 1 === this.calMonth &&
        parseInt(parts[0]) === this.calYear
    },

    isToday (day) {
      const now = new Date()
      return day === now.getDate() &&
        this.calMonth === now.getMonth() &&
        this.calYear === now.getFullYear()
    }
  }
}
</script>
